import React, { useState } from 'react'
import Select from 'components/QuestionnaireRequest/AdminNew/components/Select'
import { useForm } from 'react-hook-form'
import axios from 'lib/axios'

const FORM_TYPES = [
  { 'value': 'generic', 'label': 'generic' },
  { 'value': 'lender', 'label': 'lender' },
  { 'value': 'attorney', 'label': 'attorney / due dilligence' },
  { 'value': 'appraiser', 'label': 'appraiser' },
  { 'value': 'other', 'label': 'other' },
]

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const TableHead = () => {
  return (
    <thead>
      <tr>
        <th className="col-xs-4">
          <span>email</span>
        </th>
        <th className="col-xs-4">
          <span>type</span>
        </th>
        <th className="col-xs-4">
          <span>actions</span>
        </th>
      </tr>
    </thead>
  )
}

const EmailItemRow = ({ email, buildingId, onUpdate, onRemove }) => {
  const [localEmail, setLocalEmail] = useState(email)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const handleFormTypesChange = async (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setError('At least one type must be selected')
      return
    }

    setError('')
    const updatedEmail = { ...localEmail, selected_form_types: selectedItems }
    setLocalEmail(updatedEmail)

    setIsSaving(true)
    try {
      const url = `/buildings/${buildingId}/questionnaires/email`
      const data = {
        email: localEmail.email,
        selected_form_types: selectedItems,
      }

      const response = await axios.put(url, { questionnaire_email: data })
      setLocalEmail(response.data.email)
      onUpdate(response.data.email)
    } catch (error) {
      console.error('Error updating email:', error)
      setError('Failed to update email')
      // Revert on error
      setLocalEmail(email)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemove = async () => {
    const confirmed = window.confirm(`Are you sure you want to remove ${localEmail.email} from the list of managers?`)

    if (!confirmed) return

    setIsSaving(true)
    try {
      const url = `/buildings/${buildingId}/questionnaires/email`
      await axios.delete(url, { data: { questionnaire_manager_email_key: localEmail.questionnaire_manager_email_key } })
      onRemove(localEmail.questionnaire_manager_email_key)
    } catch (error) {
      console.error('Error removing email:', error)
      setError('Failed to remove email')
      setIsSaving(false)
    }
  }

  return (
    <tr>
      <td>
        <span>{localEmail.email}</span>
      </td>
      <td>
        <div>
          <Select
            multiple={true}
            options={FORM_TYPES}
            defaultSelectedItems={localEmail.selected_form_types || []}
            onClose={handleFormTypesChange}
            selectAll={true}
            selectAllOptionLabel="select all types"
            selectAllDisplay="all types"
            label="select type"
            disabled={isSaving}
          />
          {error && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{error}</div>}
        </div>
      </td>
      <td>
        <button className="dome-btn dome-btn-base dome-btn-red-stroke" onClick={handleRemove} disabled={isSaving}>
          remove
        </button>
      </td>
    </tr>
  )
}

const AddEmailRow = ({ buildingId, onAdd }) => {
  const [isAdding, setIsAdding] = useState(false)
  const [apiError, setApiError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
    setError,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: '',
      selected_form_types: FORM_TYPES.map((item) => item.value),
    },
  })

  const formTypes = watch('selected_form_types')

  const handleFormTypesChange = (selectedItems) => {
    if (!selectedItems || selectedItems.length === 0) {
      setError('selected_form_types', {
        type: 'manual',
        message: 'At least one type must be selected',
      })
      setValue('selected_form_types', [])
    } else {
      clearErrors('selected_form_types')
      setValue('selected_form_types', selectedItems)
    }
  }

  const onSubmit = async (data) => {
    // Additional validation for selected_form_types
    if (!data.selected_form_types || data.selected_form_types.length === 0) {
      setError('selected_form_types', {
        type: 'manual',
        message: 'At least one type must be selected',
      })
      return
    }

    setApiError('')
    setIsAdding(true)
    try {
      const url = `/buildings/${buildingId}/questionnaires/email`
      const response = await axios.post(url, {
        questionnaire_email: {
          email: data.email,
          selected_form_types: data.selected_form_types,
        },
      })

      onAdd(response.data.email)

      // Reset form
      reset({
        email: '',
        selected_form_types: FORM_TYPES.map((item) => item.value),
      })
    } catch (error) {
      console.error('Error adding email:', error)
      setApiError('Failed to add email')
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <tr className="add-email-row">
      <td>
        <div className="questionnaire-admin-pricing-input-container">
          <input
            type="text"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: EMAIL_REGEX,
                message: 'Please enter a valid email address',
              },
            })}
            placeholder="Enter Email"
          />
          {errors.email && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.email.message}</div>
          )}
        </div>
      </td>
      <td>
        <div>
          <Select
            multiple={true}
            options={FORM_TYPES}
            defaultSelectedItems={formTypes}
            onClose={handleFormTypesChange}
            selectAll={true}
            selectAllOptionLabel="select all types"
            selectAllDisplay="all types"
            label="select type"
          />
          {errors.selected_form_types && (
            <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{errors.selected_form_types.message}</div>
          )}
        </div>
      </td>
      <td>
        <div>
          <button
            className="dome-btn dome-btn-base dome-btn-aqua-stroke"
            onClick={handleSubmit(onSubmit)}
            disabled={isAdding}
          >
            + processor
          </button>
          {apiError && <div style={{ color: 'red', fontSize: '12px', marginTop: '5px' }}>{apiError}</div>}
        </div>
      </td>
    </tr>
  )
}

const TableBody = ({ emails, buildingId, onUpdate, onRemove, onAdd }) => {
  return (
    <tbody>
      {emails.map((email) => (
        <EmailItemRow
          key={email.questionnaire_manager_email_key}
          email={email}
          buildingId={buildingId}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
      <AddEmailRow buildingId={buildingId} onAdd={onAdd} />
    </tbody>
  )
}

export default function Emails({ buildingId, adminEmails: initialEmails, updateAdminEmails }) {
  const [emails, setEmails] = useState(initialEmails || [])

  const handleUpdateEmail = (updatedEmail) => {
    setEmails((prev) => {
      const updatedEmails = prev.map((email) =>
        email.questionnaire_manager_email_key === updatedEmail.questionnaire_manager_email_key ? updatedEmail : email
      )
      updateAdminEmails(updatedEmails)
      return updatedEmails
    })
  }

  const handleRemoveEmail = (emailKey) => {
    setEmails((prev) => {
      const updatedEmails = prev.filter((email) => email.questionnaire_manager_email_key !== emailKey)
      updateAdminEmails(updatedEmails)
      return updatedEmails
    })
  }

  const handleAddEmail = (newEmail) => {
    setEmails((prev) => {
      const updatedEmails = [...prev, newEmail]
      updateAdminEmails(updatedEmails)
      return updatedEmails
    })
  }

  return (
    <div id="questionnaire-request-admin-emails" className="questionnaire-manager-emails">
      <table className="dome-table dome-table-striped dome-full-width">
        <TableHead />
        <TableBody
          emails={emails}
          buildingId={buildingId}
          onUpdate={handleUpdateEmail}
          onRemove={handleRemoveEmail}
          onAdd={handleAddEmail}
        />
      </table>
    </div>
  )
}
